// Retail Ready draft overlay; fresh read-only source: live theme 176597008548.
// QA: initially unavailable -> available; HTTP failure/retry; rapid switches;
// no locations; open drawer on variant change; focus and Escape on touch/desktop.
if (!customElements.get('pickup-availability')) {
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.requestVersion = 0;
        this.abortController = null;
        this.drawer = null;
        if (this.hasAttribute('available')) this.fetchAvailability(this.dataset.variantId);
      }

      disconnectedCallback() {
        this.requestVersion += 1;
        this.abortController?.abort();
        this.clearDrawer();
      }

      clearDrawer() {
        if (this.drawer?.hasAttribute('open')) this.drawer.hide();
        this.drawer?.remove();
        this.drawer = null;
      }

      fetchAvailability(variantId) {
        if (!variantId) return;
        this.dataset.variantId = String(variantId);
        const requestVersion = ++this.requestVersion;
        this.abortController?.abort();
        this.abortController = new AbortController();
        this.clearDrawer();
        this.innerHTML = '';
        this.removeAttribute('available');
        this.setAttribute('aria-busy', 'true');

        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        // Preserve Dawn's section endpoint. Ignore late responses after a variant change.
        return fetch(variantSectionUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Pickup availability request failed');
            return response.text();
          })
          .then((text) => {
            if (requestVersion !== this.requestVersion) return;
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            if (!sectionInnerHTML) throw new Error('Pickup availability section missing');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            if (requestVersion !== this.requestVersion || e.name === 'AbortError') return;
            this.renderError();
          })
          .finally(() => {
            if (requestVersion === this.requestVersion) this.removeAttribute('aria-busy');
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      update(variant) {
        this.dataset.variantId = variant?.id ? String(variant.id) : '';
        if (variant?.available) {
          return this.fetchAvailability(variant.id);
        } else {
          this.requestVersion += 1;
          this.abortController?.abort();
          this.clearDrawer();
          this.removeAttribute('available');
          this.removeAttribute('aria-busy');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        if (!this.errorHtml) return;
        this.appendChild(this.errorHtml.cloneNode(true));
        // A failed check is unknown, not a statement that pickup is unavailable.
        const message = this.querySelector('.pickup-availability-info p');
        if (message) message.textContent = 'Could not check pickup availability.';
        const button = this.querySelector('button');
        if (button) {
          button.type = 'button';
          button.addEventListener('click', this.onClickRefreshList);
        }
      }

      renderPreview(sectionInnerHTML) {
        this.clearDrawer();
        const preview = sectionInnerHTML.querySelector('pickup-availability-preview');
        const drawer = sectionInnerHTML.querySelector('pickup-availability-drawer');
        if (!preview) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }
        if ((preview.querySelector('button') && !drawer) || (drawer && !drawer.querySelector('button'))) {
          throw new Error('Pickup availability drawer incomplete');
        }

        this.innerHTML = preview.outerHTML;
        this.setAttribute('available', '');

        this.drawer = drawer;
        if (this.drawer) {
          document.body.appendChild(this.drawer);
          const colorClassesToApply = (this.dataset.productPageColorScheme || '').split(' ').filter(Boolean);
          colorClassesToApply.forEach((colorClass) => this.drawer.classList.add(colorClass));
        }

        const button = this.querySelector('button');
        if (button && this.drawer)
          button.addEventListener('click', (evt) => {
            this.drawer?.show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.hide();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.hide();
        }
      }

      hide() {
        this.removeAttribute('open');
        document.body.removeEventListener('click', this.onBodyClick);
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.focusElement);
      }

      show(focusElement) {
        this.focusElement = focusElement;
        this.setAttribute('open', '');
        document.body.addEventListener('click', this.onBodyClick);
        document.body.classList.add('overflow-hidden');
        trapFocus(this);
      }
    }
  );
}
